/**
 * Fearnleys brand style configuration — colors, typography, and prompt
 * rules extracted from the client's template (slide master colorScheme
 * "FS Colours" + fontScheme "Custom 1" / Open Sans, layout placeholder
 * geometry, via the OOXML).
 *
 * The first layout is "Cover page 1 without subtitle full hight".
 * Unqualified body inserts use "Content One Content box" (first catalog
 * match, slideLayout15 on master 3). "Content Blank" is the most common
 * sample page but has no body box, so it is not the default.
 */

import type { ThemeColors } from "../../types";

export const FEARNLEYS_COLORS = {
  black: "000000", // dk1
  white: "FFFFFF", // lt1
  navy: "142D3C", // dk2 / accent1
  grey: "7F8183", // lt2
  blue: "014584", // accent2 / hlink
  green: "3C8E75", // accent3
  lightBlue: "B3D9FE", // accent4
  slate: "5587AF", // accent5 / folHlink
  silver: "C0C0C0", // accent6

  up: "00B050",
  down: "C00000",
} as const;

export const FEARNLEYS_THEME_COLORS: ThemeColors = {
  dark1: FEARNLEYS_COLORS.black,
  dark2: FEARNLEYS_COLORS.navy,
  light1: FEARNLEYS_COLORS.white,
  light2: FEARNLEYS_COLORS.grey,
  accent1: FEARNLEYS_COLORS.navy,
  accent2: FEARNLEYS_COLORS.blue,
  accent3: FEARNLEYS_COLORS.green,
  accent4: FEARNLEYS_COLORS.lightBlue,
  accent5: FEARNLEYS_COLORS.slate,
  accent6: FEARNLEYS_COLORS.silver,
  hyperlink: FEARNLEYS_COLORS.blue,
  followedHyperlink: FEARNLEYS_COLORS.slate,
  majorFont: "Open Sans",
  minorFont: "Open Sans",
};

export const FEARNLEYS_CHART_SERIES_PALETTE = [
  FEARNLEYS_COLORS.navy,
  FEARNLEYS_COLORS.blue,
  FEARNLEYS_COLORS.green,
  FEARNLEYS_COLORS.lightBlue,
  FEARNLEYS_COLORS.slate,
  FEARNLEYS_COLORS.silver,
];

export const FEARNLEYS_SLIDE_DIMENSIONS = { width: 960, height: 540 } as const;

export const FEARNLEYS_POSITIONS = {
  header: { left: 38.8, top: 23.2, width: 880.5, height: 21.8 },
  subhead: { left: 38.9, top: 48.0, width: 880.5, height: 15.7 },
  content: { left: 38.8, top: 85.9, width: 883.4, height: 393.8 },
  source: { left: 38.9, top: 493.2, width: 880.4, height: 8.8 },
  footer: { left: 38.8, top: 510.9, width: 666.6, height: 24.3 },
  leftColumn: { left: 38.9, top: 85.8, width: 425.2, height: 393.7 },
  rightColumn: { left: 494.2, top: 85.9, width: 425.2, height: 393.7 },
} as const;

export const FEARNLEYS_STYLE_PROMPT = `

## FEARNLEYS BRAND STYLE OVERRIDE (active — apply these rules to ALL output)

REFERENCE PRECEDENTS: The attached "Fearnleys.pptx" is the Fearnleys visual standard. Match the closest relevant precedent for covers, one-box content pages, and two-box content pages. Prefer its navy/Open Sans system and the content box under the header over inventing a generic layout.

CRITICAL FONT RULE: You MUST include \`fontFamily\` on EVERY shape you create or modify — even when editing existing text. This OVERRIDES the general rule about matching the existing presentation's fonts. Fearnleys uses:
- Titles / headings / body / bullets / tables / charts / notes / subtitles: \`"Open Sans"\`
NEVER omit fontFamily when Fearnleys style is active. If a shape already has the wrong font (including Calibri or Arial), include fontFamily to correct it to Open Sans.

### Color palette (from the Fearnleys master colorScheme "FS Colours")
Use ONLY these Fearnleys brand colors. Do NOT use arbitrary hex values.

**Theme mapping:**
- Text1 / dark1: Black #${FEARNLEYS_COLORS.black}
- Text2 / dark2: Navy #${FEARNLEYS_COLORS.navy}
- Background1 / light1: White #${FEARNLEYS_COLORS.white}
- Background2 / light2: Grey #${FEARNLEYS_COLORS.grey}
- Accent1: Navy #${FEARNLEYS_COLORS.navy}
- Accent2: Blue #${FEARNLEYS_COLORS.blue}
- Accent3: Green #${FEARNLEYS_COLORS.green}
- Accent4: Light blue #${FEARNLEYS_COLORS.lightBlue}
- Accent5: Slate #${FEARNLEYS_COLORS.slate}
- Accent6: Silver #${FEARNLEYS_COLORS.silver}

**Usage:**
- **Primary brand / emphasis**: Navy #${FEARNLEYS_COLORS.navy}
- **Secondary emphasis**: Blue #${FEARNLEYS_COLORS.blue}
- **Support**: Green #${FEARNLEYS_COLORS.green} or slate #${FEARNLEYS_COLORS.slate}
- **Text**: Navy #${FEARNLEYS_COLORS.navy} or black #${FEARNLEYS_COLORS.black}
- **Background / shading**: White #${FEARNLEYS_COLORS.white} or light blue #${FEARNLEYS_COLORS.lightBlue}
- **Up / down**: Green #${FEARNLEYS_COLORS.up} / red #${FEARNLEYS_COLORS.down}
- **Chart series palette** (in order): ${FEARNLEYS_CHART_SERIES_PALETTE.map((c) => `#${c}`).join(", ")}
  Lead with navy (#${FEARNLEYS_COLORS.navy}) as the first/primary series color.

### Typography (authoritative — from "Content One Content box")
Slide dimensions: 960 × 540 pt (16:9).

**Placeholder positions on "Content One Content box" (first catalog match):**
- **Header subtitle** (idx 1): left=${FEARNLEYS_POSITIONS.header.left}, top=${FEARNLEYS_POSITIONS.header.top}, width=${FEARNLEYS_POSITIONS.header.width}, height=${FEARNLEYS_POSITIONS.header.height} — Open Sans, about 17pt.
- **Subhead** (body idx 60): left=${FEARNLEYS_POSITIONS.subhead.left}, top=${FEARNLEYS_POSITIONS.subhead.top}, width=${FEARNLEYS_POSITIONS.subhead.width}, height=${FEARNLEYS_POSITIONS.subhead.height} — Open Sans, about 13pt.
- **Content** (idx 61): left=${FEARNLEYS_POSITIONS.content.left}, top=${FEARNLEYS_POSITIONS.content.top}, width=${FEARNLEYS_POSITIONS.content.width}, height=${FEARNLEYS_POSITIONS.content.height} — Open Sans, about 10pt.
- **Source line** (body idx 58, shape name "Plassholder for tekst 3", sample text "SOURCE / NOTE:"): left=${FEARNLEYS_POSITIONS.source.left}, top=${FEARNLEYS_POSITIONS.source.top}, width=${FEARNLEYS_POSITIONS.source.width}, height=${FEARNLEYS_POSITIONS.source.height} — about 7pt.
- **Footer** (ftr idx 11): left=${FEARNLEYS_POSITIONS.footer.left}, top=${FEARNLEYS_POSITIONS.footer.top}, width=${FEARNLEYS_POSITIONS.footer.width}, height=${FEARNLEYS_POSITIONS.footer.height}.

**Two-column sibling "Content Two Content boxes":**
- Left body: left=${FEARNLEYS_POSITIONS.leftColumn.left}, top=${FEARNLEYS_POSITIONS.leftColumn.top}, width=${FEARNLEYS_POSITIONS.leftColumn.width}, height=${FEARNLEYS_POSITIONS.leftColumn.height}
- Right body: left=${FEARNLEYS_POSITIONS.rightColumn.left}, top=${FEARNLEYS_POSITIONS.rightColumn.top}, width=${FEARNLEYS_POSITIONS.rightColumn.width}, height=${FEARNLEYS_POSITIONS.rightColumn.height}

**Layout choice:**
- Default body: "Content One Content box". The name is duplicated; the first catalog match is the geometry above.
- Do not default to "Content Blank". It is the most common sample page, but it is only a title, subhead, and footer — there is no body box.
- Do not default to "Cover page 1 without subtitle full hight" (the first layout). Covers keep the "Private and confidential" and "Month year" placeholders. The master instruction "CHANGE TITLE IN MENU, INSERT; HEADER AND FOOTER" is not slide content.

### Charts (Fearnleys style)
- Font: Open Sans, minimum 8pt
- **Primary data color**: navy (#${FEARNLEYS_COLORS.navy}); multi-series uses the series palette above in order
- Data labels: positioned outside bars/columns (inside for stacked)
- Gridlines: minimal — none by default; only when no data labels are shown
- Legend: bottom-center placement (only when needed for multi-series)
- No 3D effects, gradients, or graphic fills

### Tables (Fearnleys style)
- Header row: navy (#${FEARNLEYS_COLORS.navy}) fill with white text, bold
- Body: white fill, navy or black Open Sans text (about 10pt)
- Alternate-row shading: light blue (#${FEARNLEYS_COLORS.lightBlue})
- Borders: thin silver (#${FEARNLEYS_COLORS.silver}); align numeric columns consistently

### Shapes & layout (Fearnleys style)
- Separate content with sharp rectangular blocks — not rounded shapes
- Keep source lines and the footer on the baseline of the content layout
- Prefer "Content One Content box" for ordinary body slides and "Content Two Content boxes" only when the page is genuinely two columns

**TEXT FITTING RULE**: Keep labels and item descriptions SHORT (max 6-8 words each). The content box is wide; long sentences still overflow the 10pt body.
`;
